<template>
    <div class="flex gap-6 laboratory-edit-horse-outer">
        <div class="laboratory-edit-horse flex flex-column gap-6 w-11">
            <h2 class="laboratory-edit-horse-header">{{ $t('message.laboratoryHorses.form.header') }} ({{ horse.name }})</h2>

            <div v-if="computedLaboratoryTest" class="first-section">
                <div v-for="(dnaSample, index) in horse.laboratoryTest" class="flex flex-column justify-content-center">
                    <h2 class="section-header dna-header">
                        {{ $t('message.laboratoryHorses.form.dna') }} #{{ horse.laboratoryTest.length - index }}
                    </h2>
                    <div class="flex section-details">
                        <div class="w-6 flex flex-column gap-3 section-details-inner">
                            <div class="data-box">
                                <span class="uppercase label-font">{{ $t('message.laboratoryHorses.form.samplesFrom') }}</span>
                                {{ dnaSample.dnaSampleTypes.join(', ') }}
                            </div>
                            <div class="data-box">
                                <span class="uppercase label-font">{{ $t('message.laboratoryHorses.form.batchRef') }}</span>
                                {{ dnaSample.batchRefNumber }}
                            </div>
                            <div class="data-box">
                                <span class="uppercase label-font">{{ $t('message.laboratoryHorses.form.testReq') }}</span>
                                {{ computeTestTypeLabel(dnaSample.testType) }}
                            </div>
                            <div class="data-box">
                                <span class="uppercase label-font">{{ $t('message.laboratoryHorses.form.testPrior') }}</span>
                                {{ dnaSample.testPriority }}
                            </div>
                            <div class="data-box">
                                <span class="uppercase label-font">{{ $t('message.laboratoryHorses.form.shipment') }}</span>
                                {{ dnaSample.shipmentDate }}
                            </div>
                        </div>
                        <div class="w-6 gap-3 flex flex-column section-details-inner">
                            <div class="data-box">
                                <span class="uppercase label-font">{{ $t('message.laboratoryHorses.form.dnaRef') }}</span>
                                {{ dnaSample.dnaReferenceNumber }}
                            </div>
                            <div class="data-box">
                                <span class="uppercase label-font">{{ $t('message.laboratoryHorses.form.receipt') }}</span>
                                {{ dnaSample.shipmentReceivedDate }}
                            </div>
                            <div class="data-box">
                                <span class="uppercase label-font">{{ $t('message.laboratoryHorses.form.comments') }}</span>
                                {{ dnaSample.notes }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div v-else>
                <h2 class="section-header dna-header">
                    {{ $t('message.laboratoryHorses.form.emptyTable') }}
                </h2>
            </div>
            <div class="flex second-section section-details">
                <div class="w-6 flex flex-column gap-3 section-details-inner">
                    <h2 class="section-header parents-header m-0">{{ $t('message.laboratoryHorses.form.sire') }}</h2>
                    <div class="data-box">
                        <span class="uppercase label-font">{{ $t('message.laboratoryHorses.form.name') }}</span>
                        {{ horse.father?.name }}
                    </div>
                    <div class="data-box">
                        <span class="uppercase label-font">{{ $t('message.laboratoryHorses.form.horseId') }}</span>
                        {{ horse.father?.ueln }}
                    </div>
                    <div class="data-box">
                        <span class="uppercase label-font">{{ $t('message.laboratoryHorses.form.coatColor') }}</span>
                        {{ horse.father?.color }}
                    </div>
                    <div v-if="horse.father?.birthCountry === 'Libya'" class="data-box">
                        <span class="uppercase label-font">{{ $t('message.laboratoryHorses.form.dnaRef') }}</span>
                        {{ horse.father?.dnaReference }}
                    </div>
                    <div v-if="horse.damDnaDocumentUrl" class="data-box">
                        <span class="uppercase label-font">{{ $t('message.laboratoryHorses.form.dnaDoc') }}</span>
                        <a :href="horse.damDnaDocumentUrl" download target="_blank"
                           v-tooltip.top="$t('message.horseCreate.downloadDnaCard')"  style="color: #192134">
                            <i class="pi pi-cloud-download download-button"/>
                        </a>
                    </div>
                </div>
                <div class="w-6 flex flex-column gap-3 section-details-inner">
                    <h2 class="section-header parents-header m-0">{{ $t('message.laboratoryHorses.form.dam') }}</h2>
                    <div class="data-box">
                        <span class="uppercase label-font">{{ $t('message.laboratoryHorses.form.name') }}</span>
                        {{ horse.mother?.name }}
                    </div>
                    <div class="data-box">
                        <span class="uppercase label-font">{{ $t('message.laboratoryHorses.form.horseId') }}</span>
                        {{ horse.mother?.ueln }}
                    </div>
                    <div class="data-box">
                        <span class="uppercase label-font">{{ $t('message.laboratoryHorses.form.coatColor') }}</span>
                        {{ horse.mother?.color }}
                    </div>
                    <div v-if="horse.mother?.birthCountry === 'Libya'" class="data-box">
                        <span class="uppercase label-font">{{ $t('message.laboratoryHorses.form.dnaRef') }}</span>
                        {{ horse.mother?.dnaReference }}
                    </div>
                    <div v-if="horse.sireDnaDocumentUrl" class="data-box">
                        <span class="uppercase label-font">{{ $t('message.laboratoryHorses.form.dnaDoc') }}</span>
                        <a :href="horse.sireDnaDocumentUrl" download target="_blank"
                           v-tooltip.top="$t('message.horseCreate.downloadDnaCard')"  style="color: #192134">
                            <i class="pi pi-cloud-download download-button"/>
                        </a>
                    </div>
                </div>
            </div>

            <div class="flex flex-column third-section">
                <h2 class="section-header mt-0">{{ horse.name }}</h2>
                <div class="flex align-items-center section-details">
                    <div class="w-6 flex flex-column gap-3 section-details-inner">
                        <div class="data-box">
                            <span class="uppercase label-font">{{ $t('message.laboratoryHorses.form.horseId') }}</span>
                            {{ horse.ueln }}
                        </div>
                        <div class="data-box">
                            <span class="uppercase label-font">{{ $t('message.laboratoryHorses.form.gender') }}</span>
                            {{ horse.sex }}
                        </div>
                        <div class="data-box">
                            <span class="uppercase label-font">{{ $t('message.laboratoryHorses.form.coatColor') }}</span>
                            {{ horse.colorName }}
                        </div>
                        <div class="data-box">
                            <span class="uppercase label-font" >{{$t('message.laboratoryHorses.form.dnaCard')}}
                                <a :href="horse.horseDnaDocumentUrl"
                                   download target="_blank" v-tooltip.top="$t('message.horseCreate.downloadDnaCard')"
                                   style="color: #192134">
                                    <i class="pi pi-cloud-download download-button"/>
                                </a>
                            </span>
                        </div>
                    </div>
                    <div class="w-6 flex flex-column gap-3 test-box section-details-inner">
                        <div class="data-box">
                            <span class="uppercase label-font">{{ $t('message.laboratoryHorses.form.testRes') }}</span>
                            <!-- {{ horse.sex }} -->
                        </div>
                        <div v-if="horse.why" class="data-box">
                            <span class="uppercase label-font">{{ $t('message.laboratoryHorses.form.why') }}</span>
                             {{ horse.why }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import {getTestType} from "@/swagger/vue-api-client";
    import Tooltip from "primevue/tooltip";

    export default {
        name: "HorseEditMainView",

        components: {},

        props: {
            horse: null,
        },

        data() {
            return {
                testTypes: [],
            };
        },

        beforeMount() {
            getTestType().then((res) => {
                this.testTypes = res.data;
            });
        },

        directives: {
            tooltip: Tooltip,
        },

        computed: {
            computedLaboratoryTest() {
                return this.horse.laboratoryTest.length > 0;
            }
        },

        methods: {
            computeTestTypeLabel(type) {
                const foundType = this.testTypes?.find(t => t.value === type);
                return foundType ? foundType.label : '';
            }
        },
    };
</script>

<style lang="scss" scoped>
    .laboratory-edit-horse {
        font-family: var(--decor-font-family);
        .test-box {
            background-color: var(--primary-color);
            padding: 1.5rem;
            border-radius: var(--border-radius);
        }
        &-header {
            color: var(--accent-color);
            font-weight: 500;
            font-size: 35px;
            font-family: var(--decor-font-family);
        }
        .section-header {
            font-size: 35px;
            &.dna-header {
                font-size: 25px;
                color: var(--accent-color);
            }
            &-parents-header {
                font-size: 30px;
            }
        }
        .data-box {
            font-size: 25px;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            .label-font {
                font-family: var(--font-family);
                font-size: 14px;
                line-height: 14px;
            }
        }
        .first-section, .second-section {
            position: relative;
            &::after {
                content: "";
                border: 1px solid var(--separator-color);
                position: absolute;
                bottom: -2rem;
                left: 0;
                width: 100%;
            }
        }
        // @media screen and (max-width: 1000px) {
        //     .section-details {
        //         flex-direction: column;
        //         align-items: start !important;
        //         gap: 2rem;
        //         &-inner {
        //             width: 100% !important;
        //         }
        //     }
        // }
    }
    // @media screen and (max-width: 650px) {
    //     .laboratory-horse-test-outer {
    //         margin: 20px 10px;
    //     }
    // }

    a > i.download-button {
        font-size: 24px;
    }
</style>
